import React, { useState, useEffect, useCallback } from 'react';
import { useWheel } from './hooks/useWheel';
import { useTheme } from './hooks/useTheme';
import { useAudio } from './hooks/useAudio';
import { useSpinController } from './hooks/useSpinController';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from './db/database';
import { DEFAULT_SETTINGS, updateSettings } from './db/settingsRepo';
import { WheelEntry, AppSettings } from './types';

// Components
import { AppHeader } from './components/Layout/AppHeader';
import { WheelSidebar } from './components/Sidebar/WheelSidebar';
import { SpinWheelCanvas } from './components/Wheel/SpinWheelCanvas';
import { WheelPointer } from './components/Wheel/WheelPointer';
import { SpinCenterButton } from './components/Wheel/SpinCenterButton';
import { WheelControls } from './components/Wheel/WheelControls';
import { EntryPanel } from './components/Entries/EntryPanel';
import { WinnerModal } from './components/Winner/WinnerModal';
import { SettingsModal } from './components/Settings/SettingsModal';
import { ShortcutsModal } from './components/Common/ShortcutsModal';

export const App: React.FC = () => {
  // DB & Preferences
  const settings: AppSettings = useLiveQuery(() => db.settings.get('global_settings'), []) || DEFAULT_SETTINGS;
  const { theme, isDark, toggleTheme } = useTheme();
  const {
    soundEnabled,
    toggleSound,
    playTick,
    playSpinStart,
    playStopClack,
    playWinnerFanfare,
    playButtonClick,
  } = useAudio();

  // Wheels & Data
  const {
    wheels,
    currentWheel,
    activeWheelId,
    entries,
    history,
    selectWheel,
    createNewWheel,
    updateCurrentWheel,
    duplicateCurrentWheel,
    deleteCurrentWheel,
    addEntry,
    bulkAddEntries,
    updateEntry,
    deleteEntry,
    clearAllEntries,
    shuffleEntries,
    sortEntriesAZ,
    deduplicateEntries,
    logWinner,
    deleteHistory,
    clearHistory,
  } = useWheel();

  // Modals state - on mobile (< 1024px), sidebar is closed by default to focus on the wheel
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1024;
    }
    return true;
  });
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isShortcutsOpen, setIsShortcutsOpen] = useState(false);
  const [winnerModalOpen, setWinnerModalOpen] = useState(false);
  const [celebrationWinner, setCelebrationWinner] = useState<WheelEntry | null>(null);

  // Active wheel configuration
  const spinDuration = currentWheel?.spinDuration || settings.defaultSpinDuration || 5;
  const currentPalette = currentWheel?.colorThemeId || settings.colorPalette || 'vibrant';
  const removeWinnerSetting = currentWheel?.removeWinnerAfterSpin ?? settings.removeWinnerAfterSpin;

  // Spin Controller
  const handleWinnerSelected = useCallback(
    async (winner: WheelEntry, _index: number) => {
      // 1. Log to history
      await logWinner(winner.id, winner.label);
      // 2. Play celebratory fanfare
      playWinnerFanfare();
      // 3. Open celebratory modal
      setCelebrationWinner(winner);
      setWinnerModalOpen(true);
    },
    [logWinner, playWinnerFanfare]
  );

  const {
    spinState,
    rotationDeg,
    pointerDeflectionDeg,
    isSpinning,
    spin,
    resetSpin,
  } = useSpinController({
    entries,
    duration: spinDuration,
    onSpinStart: playSpinStart,
    onTick: playTick,
    onStop: playStopClack,
    onWinnerSelected: handleWinnerSelected,
  });

  // Handle Remove and Spin
  const handleRemoveAndSpin = useCallback(
    async (entryId: string) => {
      await deleteEntry(entryId);
      // Brief delay before launching next spin
      setTimeout(() => {
        spin();
      }, 400);
    },
    [deleteEntry, spin]
  );

  // Handle Duration & Palette quick changes
  const handleDurationChange = async (newDuration: number) => {
    await updateCurrentWheel({ spinDuration: newDuration });
  };

  const handlePaletteChange = async (newPalette: string) => {
    await updateCurrentWheel({ colorThemeId: newPalette });
  };

  // Keyboard Shortcuts Listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if inside text inputs or textareas
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      if (e.code === 'Space') {
        e.preventDefault();
        if (!isSpinning && entries.length >= 2) {
          playButtonClick();
          spin();
        }
      } else if (e.key === 'm' || e.key === 'M') {
        e.preventDefault();
        toggleSound();
      } else if (e.key === 's' || e.key === 'S') {
        e.preventDefault();
        if (!isSpinning) {
          playButtonClick();
          shuffleEntries();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSpinning, entries.length, spin, toggleSound, shuffleEntries, playButtonClick]);

  const disabledReason =
    entries.length === 0
      ? 'Add at least 2 entries to spin'
      : entries.length === 1
      ? 'Add 1 more entry to spin'
      : undefined;

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        minHeight: '100dvh',
        width: '100%',
        maxWidth: '100vw',
        overflow: 'hidden',
        background: 'var(--bg-primary)',
        color: 'var(--text-primary)',
        position: 'relative',
      }}
    >
      {/* 1. Mobile Drawer Backdrop Overlay */}
      {isSidebarOpen && (
        <div
          className="drawer-backdrop"
          onClick={() => setIsSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* 1. Left Sidebar (Collapsible / Mobile Drawer) */}
      {isSidebarOpen && (
        <div
          style={{
            width: '280px',
            flexShrink: 0,
            padding: '1rem 0 1rem 1rem',
            height: '100%',
            transition: 'all 0.3s ease',
          }}
          className="sidebar-wrapper mobile-drawer"
        >
          <WheelSidebar
            wheels={wheels}
            activeWheelId={activeWheelId}
            onSelectWheel={selectWheel}
            onCreateWheel={createNewWheel}
            onRenameWheel={(id, name) => {
              if (id === activeWheelId) updateCurrentWheel({ name });
              else db.wheels.update(id, { name, updatedAt: Date.now() });
            }}
            onDuplicateWheel={duplicateCurrentWheel}
            onDeleteWheel={deleteCurrentWheel}
            onOpenSettings={() => setIsSettingsOpen(true)}
            onOpenShortcuts={() => setIsShortcutsOpen(true)}
            currentEntries={entries}
            isSpinning={isSpinning}
            onClose={() => setIsSidebarOpen(false)}
          />
        </div>
      )}

      {/* 2. Main Studio Canvas Area */}
      <main
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflowY: 'auto',
          padding: 'clamp(0.5rem, 2.5vw, 1rem)',
          gap: '0.75rem',
          minWidth: 0,
        }}
      >
        {/* Header */}
        <AppHeader
          currentWheel={currentWheel}
          onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
          soundEnabled={soundEnabled}
          onToggleSound={toggleSound}
          isDark={isDark}
          onToggleTheme={toggleTheme}
          onOpenSettings={() => setIsSettingsOpen(true)}
        />

        {/* 2-Column Split: Center Wheel Stage & Right Entries Panel */}
        <div
          style={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) minmax(280px, 360px)',
            gap: 'clamp(0.75rem, 2vw, 1.25rem)',
            alignItems: 'stretch',
            minHeight: 0,
          }}
          className="stage-grid"
        >
          {/* Wheel Stage (Center Visual Focus) */}
          <section
            className="glass-panel"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'clamp(0.75rem, 3vw, 1.5rem)',
              gap: 'clamp(0.75rem, 2vw, 1.25rem)',
              position: 'relative',
              boxShadow: 'var(--wheel-shadow)',
              minHeight: 'auto',
              width: '100%',
              minWidth: 0,
            }}
          >
            {/* Wheel Canvas with Top Pointer */}
            <div
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                flex: 1,
              }}
            >
              <WheelPointer
                deflectionDeg={pointerDeflectionDeg}
                isSpinning={isSpinning}
              />
              <SpinWheelCanvas
                entries={entries}
                rotationDeg={rotationDeg}
                paletteId={currentPalette}
                isSpinning={isSpinning}
                onCenterClick={() => {
                  if (!isSpinning && entries.length >= 2) {
                    playButtonClick();
                    spin();
                  }
                }}
                disabled={entries.length < 2}
                wheelNameFontSize={settings.wheelNameFontSize ?? 24}
              />
            </div>

            {/* Large Primary Spin Action */}
            <SpinCenterButton
              onSpin={() => {
                playButtonClick();
                spin();
              }}
              isSpinning={isSpinning}
              disabled={entries.length < 2}
              disabledReason={disabledReason}
            />

            {/* Quick Wheel Controls (Duration, Palette, Shuffle, Audio) */}
            <WheelControls
              duration={spinDuration}
              onDurationChange={handleDurationChange}
              onShuffle={() => {
                playButtonClick();
                shuffleEntries();
              }}
              soundEnabled={soundEnabled}
              onToggleSound={toggleSound}
              paletteId={currentPalette}
              onPaletteChange={handlePaletteChange}
              isSpinning={isSpinning}
              totalEntries={entries.length}
            />
          </section>

          {/* Right Entries and History Manager */}
          <section style={{ height: '100%', minHeight: 0, width: '100%', minWidth: 0 }}>
            <EntryPanel
              entries={entries}
              history={history}
              paletteId={currentPalette}
              onAddEntry={(label) => {
                playButtonClick();
                addEntry(label);
              }}
              onBulkAdd={(labels) => {
                playButtonClick();
                bulkAddEntries(labels);
              }}
              onUpdateEntry={updateEntry}
              onDeleteEntry={deleteEntry}
              onDuplicateEntry={(label, color) => addEntry(label, color)}
              onShuffle={() => {
                playButtonClick();
                shuffleEntries();
              }}
              onSortAZ={() => {
                playButtonClick();
                sortEntriesAZ();
              }}
              onDeduplicate={async () => {
                playButtonClick();
                const removed = await deduplicateEntries();
                if (removed > 0) {
                  alert(`Removed ${removed} duplicate ${removed === 1 ? 'entry' : 'entries'}.`);
                }
              }}
              onClearAll={clearAllEntries}
              onDeleteHistoryItem={deleteHistory}
              onClearHistory={clearHistory}
              isSpinning={isSpinning}
            />
          </section>
        </div>
      </main>

      {/* 3. Celebration Modal */}
      <WinnerModal
        isOpen={winnerModalOpen}
        winner={celebrationWinner}
        onClose={() => {
          setWinnerModalOpen(false);
          resetSpin();
        }}
        onSpinAgain={() => {
          setWinnerModalOpen(false);
          resetSpin();
          setTimeout(() => spin(), 250);
        }}
        onRemoveAndSpin={(id) => {
          setWinnerModalOpen(false);
          resetSpin();
          handleRemoveAndSpin(id);
        }}
        confettiEnabled={settings.confettiEnabled}
      />

      {/* 4. Settings Modal */}
      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        settings={settings}
        onUpdateSettings={updateSettings}
      />

      {/* 5. Shortcuts Modal */}
      <ShortcutsModal
        isOpen={isShortcutsOpen}
        onClose={() => setIsShortcutsOpen(false)}
      />

      {/* Responsive media query tweaks */}
      <style>{`
        @media (max-width: 1023px) {
          .stage-grid {
            grid-template-columns: 1fr !important;
          }
          .sidebar-wrapper {
            position: fixed !important;
            left: 0;
            top: 0;
            bottom: 0;
            z-index: 90;
            width: min(320px, 85vw) !important;
            max-width: 85vw !important;
            height: 100% !important;
            background: var(--bg-primary);
            box-shadow: 0 0 50px rgba(0,0,0,0.6);
            animation: slideInLeft 0.25s cubic-bezier(0.16, 1, 0.3, 1);
            padding: 0.75rem 0 0.75rem 0.75rem !important;
          }
        }
        @media (min-width: 1024px) {
          .sidebar-wrapper {
            position: static !important;
            width: 280px !important;
            box-shadow: none !important;
            animation: none !important;
            padding: 1rem 0 1rem 1rem !important;
          }
          .drawer-backdrop {
            display: none !important;
          }
        }
        @media (min-width: 1200px) {
          .desktop-desc {
            display: inline !important;
          }
        }
      `}</style>
    </div>
  );
};

export default App;
